import React from "react";
import { BiLogoVenmo } from "react-icons/bi";
import { SiCashapp, SiPaypal, SiZelle } from "react-icons/si";
import { BsCashStack } from "react-icons/bs";
import { TbFlagQuestion } from "react-icons/tb";

/**
 * Single source of truth for how a transaction's payment method is shown.
 *
 * Every surface that displays a payment type — the transaction-detail row
 * and its method picker, the add-transaction tiles, and the unsettled
 * buy-in rows — reads its logo, label, and css class from here, so the
 * brand marks (Venmo, Cash App, PayPal, Zelle) and the cash symbol stay
 * consistent everywhere.
 *
 * The icons are monochrome react-icons that inherit `currentColor` and size
 * to `1em`, so each call site tints/sizes them via its own typography.
 *
 * Listed in display order; pickers and grids map over PAYMENT_METHODS.
 */
export const PAYMENT_METHODS = [
    { type: "CASH", label: "Cash", className: "cash", Icon: BsCashStack },
    { type: "VENMO", label: "Venmo", className: "venmo", Icon: BiLogoVenmo },
    { type: "PAYPAL", label: "PayPal", className: "paypal", Icon: SiPaypal },
    { type: "CASH_APP", label: "Cash App", className: "cash-app", Icon: SiCashapp },
    { type: "ZELLE", label: "Zelle", className: "zelle", Icon: SiZelle },
];

const BY_TYPE = Object.fromEntries(PAYMENT_METHODS.map((m) => [m.type, m]));

/** The css modifier class for a method chip (e.g. `cash`, `venmo`). */
export function methodClass(type) {
    return BY_TYPE[type]?.className ?? "unknown";
}

/** The human label for a method (e.g. "Cash App"). */
export function paymentLabel(type) {
    return BY_TYPE[type]?.label ?? "—";
}

/**
 * The brand logo (or cash symbol) for a payment method. Unknown/unset
 * types fall back to a question-flag icon. Extra props (title, size, …)
 * pass through to the underlying react-icon.
 */
export function MethodGlyph({ type, ...props }) {
    const Icon = BY_TYPE[type]?.Icon ?? TbFlagQuestion;
    return <Icon {...props} />;
}
